import { stopAll, type StopHandle } from '@/util/stopHandle';
import { degToRad } from '@/util/math';
import { DoubleSide, Euler, Matrix4, MeshBasicMaterial } from 'three';
import { markRaw, watchEffect } from 'vue';
import {
    imagePlaneNode,
    type ImagePlaneNode,
    updateImagePlane,
} from '@/lib/threeScene/nodes/imagePlane';
import { watchImmediate } from '@vueuse/core';
import type { PlannerStore } from '@/views/3d-planner/store/plannerStore';
import type { PlannerSceneViewModel } from '@/views/3d-planner/store/plannerSceneViewModel.types';
import type { CatstackPlaneId } from '@/domain/anatomy/catstackPlane';
import type { PlannerSceneContext } from '@/views/3d-planner/scene/plannerSceneContext';
import {
    catstackImageTextureState,
    type CatstackImageTextureState,
    updateCatstackImageTexture,
} from '@/lib/threeScene/materials/catStackImageMaterial';

export type PlannerCatstackPlaneId = CatstackPlaneId;

export type CatstackImageNode = ImagePlaneNode & {
    planeId: PlannerCatstackPlaneId;
    textureState: CatstackImageTextureState;
};

function _planeRotation(id: PlannerCatstackPlaneId): Matrix4 {
    switch (id) {
        case 'axial':
            return new Matrix4().makeRotationFromEuler(new Euler(degToRad(180), 0, 0));
        case 'coronal':
            return new Matrix4().makeRotationFromEuler(new Euler(degToRad(90), 0, 0));
        case 'sagittal':
            return new Matrix4().makeRotationFromEuler(new Euler(degToRad(90), degToRad(90), 0));
    }
}

export function catstackImageNode(
    id: PlannerCatstackPlaneId,
    properties?: Partial<CatstackImageNode>,
): CatstackImageNode {
    return {
        ...imagePlaneNode(id, { transform: _planeRotation(id), visible: false, ...properties }),
        planeId: id,
        textureState: catstackImageTextureState(properties?.textureState),
    };
}

export function updateCatstackImage(
    context: PlannerSceneContext,
    model: PlannerStore,
    scene: PlannerSceneViewModel,
    node: CatstackImageNode,
): StopHandle {
    const material = markRaw(new MeshBasicMaterial({ side: DoubleSide }));
    node.material = material;

    return stopAll(
        updateImagePlane(context, node),
        _updateSize(model, node),
        _updatePosition(model, scene, node),
        _updateVisibility(scene, node),
        _updateTextureState(model, scene, node),
        updateCatstackImageTexture(node.textureState),
        watchImmediate(
            () => node.textureState.texture,
            (texture) => (material.map = texture),
        ),
        // The material isn't owned by the scene-graph teardown; release it
        // here on stop. Three catstack planes would otherwise leak three
        // MeshBasicMaterials every scene reload — see
        // docs/threejs-practices.md disposal. The bound texture is owned by
        // `catstackImageTextureState` (disposed inside its own stopHandle),
        // so we deliberately don't touch `material.map` here.
        () => material.dispose(),
    );
}

function _updatePosition(
    model: PlannerStore,
    scene: PlannerSceneViewModel,
    node: CatstackImageNode,
): StopHandle {
    return watchEffect(() => {
        if (model.catstacksData !== null) {
            const data = model.catstacksData[node.planeId];
            const sliceSpacing = data.axis.length / (data.catstack.count - 1);
            const offset = sliceSpacing * scene.catstacks[node.planeId].sliceIndex;
            node.transform.setPosition(
                data.axis.direction.clone().multiplyScalar(offset).add(data.axis.origin),
            );
        }
    });
}

function _updateSize(model: PlannerStore, node: CatstackImageNode): StopHandle {
    return watchImmediate(
        () => (model.catstacksData ? model.catstacksData[node.planeId].catstack.world : null),
        (size) => {
            if (size) {
                node.width = size.width;
                node.height = size.height;
            }
        },
    );
}

function _updateVisibility(scene: PlannerSceneViewModel, node: CatstackImageNode): StopHandle {
    return watchImmediate(
        () => scene.catstacks.rowOpen && scene.catstacks[node.planeId].visible,
        (visible) => {
            node.visible = visible;
        },
    );
}

function _updateTextureState(
    model: PlannerStore,
    scene: PlannerSceneViewModel,
    node: CatstackImageNode,
): StopHandle {
    return stopAll(
        watchImmediate(
            () => (model.catstacksData ? model.catstacksData[node.planeId].catstack : null),
            (data) => (node.textureState.data = data),
        ),
        watchImmediate(
            () => scene.catstacks[node.planeId].sliceIndex,
            (index) => {
                node.textureState.sliceIndex = index;
            },
        ),
    );
}
